import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { ConnectionService } from './connection.service';

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: { id: string; email: string; name: string };
}

interface UserProfile {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
  createdAt: string;
}

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: 'cloud' | 'guest';
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly conn = inject(ConnectionService);

  readonly currentUser = signal<SessionUser | null>(null);

  constructor() {
    const user = localStorage.getItem('rf_user');
    if (!user) {
      return;
    }

    const sessionUser = JSON.parse(user) as SessionUser;
    this.currentUser.set(sessionUser);

    if (sessionUser.role === 'guest') {
      this.conn.setMode('lan');
      return;
    }

    this.conn.setMode('cloud');
  }

  login(email: string, password: string) {
    return this.http
      .post<AuthResponse>(`${environment.apiUrl}/auth/login`, { email, password })
      .pipe(tap((res) => this.saveSession(res)));
  }

  register(name: string, email: string, password: string) {
    return this.http
      .post<AuthResponse>(`${environment.apiUrl}/auth/register`, { name, email, password })
      .pipe(tap((res) => this.saveSession(res)));
  }

  updateProfile(name: string, email: string) {
    return this.http
      .patch<UserProfile>(`${environment.apiUrl}/account/me`, { name, email })
      .pipe(tap((user) => {
        const updated: SessionUser = { id: user.id, email: user.email, name: user.name, role: 'cloud' };
        localStorage.setItem('rf_user', JSON.stringify(updated));
        this.currentUser.set(updated);
      }));
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.http.post(`${environment.apiUrl}/account/change-password`, { currentPassword, newPassword });
  }

  refresh() {
    const refreshToken = localStorage.getItem('rf_refresh_token');
    return this.http
      .post<{ accessToken: string; refreshToken: string }>(
        `${environment.apiUrl}/auth/refresh`,
        { refreshToken },
      )
      .pipe(
        tap((res) => {
          localStorage.setItem('rf_access_token', res.accessToken);
          localStorage.setItem('rf_refresh_token', res.refreshToken);
        }),
      );
  }

  logout() {
    const refreshToken = localStorage.getItem('rf_refresh_token');
    if (refreshToken && !this.isGuest()) {
      this.http
        .post(`${environment.apiUrl}/auth/logout`, { refreshToken })
        .subscribe({ error: () => { } });
    }
    localStorage.removeItem('rf_access_token');
    localStorage.removeItem('rf_refresh_token');
    localStorage.removeItem('rf_guest_mode');
    localStorage.removeItem('rf_user');
    this.currentUser.set(null);
    this.conn.setMode('cloud');
    void this.router.navigate(['/']);
  }

  readonly isLoggedIn = computed(() => this.currentUser()?.role === 'cloud');
  readonly isGuest = computed(() => this.currentUser()?.role === 'guest');
  readonly isAuthenticated = computed(() => !!this.currentUser());

  loginAsGuest(lanIp: string, displayName?: string): void {
    const trimmedIp = lanIp.trim();
    const trimmedName = displayName?.trim();
    this.conn.setLanIp(trimmedIp);
    this.conn.setMode('lan');
    localStorage.removeItem('rf_access_token');
    localStorage.removeItem('rf_refresh_token');
    localStorage.setItem('rf_guest_mode', 'true');

    const guestUser: SessionUser = {
      id: 'guest-lan',
      email: 'Modo LAN',
      name: trimmedName || 'Invitado',
      role: 'guest',
    };

    localStorage.setItem('rf_user', JSON.stringify(guestUser));
    this.currentUser.set(guestUser);
  }

  getAccessToken(): string | null {
    return localStorage.getItem('rf_access_token');
  }

  private saveSession(res: AuthResponse): void {
    localStorage.setItem('rf_access_token', res.accessToken);
    localStorage.setItem('rf_refresh_token', res.refreshToken);
    localStorage.removeItem('rf_guest_mode');
    this.conn.setMode('cloud');
    const cloudUser: SessionUser = { ...res.user, role: 'cloud' };
    localStorage.setItem('rf_user', JSON.stringify(cloudUser));
    this.currentUser.set(cloudUser);
  }
}
